import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import type { Editor } from '@tiptap/react';
import { useEffect, useRef } from 'react';

import { getCurrentAgentRunId, publishAgentRunEvent, useEditorState } from '../../run/agent-run-store';

// Resolves local review state when a durable `pendingReview` is cleared from another tab. Edge-only
// (fires on a true->false `hasPendingReview` transition, never on a level check): a proven commit-lag
// window can leave the run store in `'review'` while this tab's `document.pendingReview` prop still
// lags the just-committed review, so a level check would false-fire during that window.
export function useExternalReviewResolution({
  requestId,
  documentKey,
  hasPendingReview,
  editor,
}: {
  requestId: string;
  documentKey?: string;
  hasPendingReview: boolean;
  editor: Editor | null;
}): void {
  const editorState = useEditorState(requestId);

  const previousHasPendingReviewRef = useRef(hasPendingReview);
  const documentKeyRef = useRef(documentKey);
  const editorRef = useRef(editor);
  editorRef.current = editor;
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  useEffect(() => {
    // A document switch resets tracking to the incoming value without firing, so a stale review from
    // the previous note cannot look like a transition on the new one.
    if (documentKeyRef.current !== documentKey) {
      documentKeyRef.current = documentKey;
      previousHasPendingReviewRef.current = hasPendingReview;
      return;
    }

    const wasPending = previousHasPendingReviewRef.current;
    previousHasPendingReviewRef.current = hasPendingReview;

    if (!wasPending || hasPendingReview) {
      return;
    }

    resolveExternalReview(requestId, editorRef.current, editorStateRef.current);
  }, [requestId, documentKey, hasPendingReview]);
}

function resolveExternalReview(requestId: string, editor: Editor | null, editorState: string): void {
  const runId = getCurrentAgentRunId(requestId);
  if (runId) {
    // The reducer no-ops unless the run is currently in review, so this only clears a latched
    // `reviewPending`. Telemetry (`note_generation_reviewed`) is intentionally not emitted here — the
    // accepting tab already emitted it.
    publishAgentRunEvent(requestId, runId, { type: 'review_resolved' });
  }

  // Leave decorations untouched while a run is actively writing so a mid-stream new run's streamed
  // preview is never disturbed.
  if (editorState === 'agent_writing' || !editor || editor.isDestroyed) {
    return;
  }

  const toolkit = getAiToolkit(editor);
  if (!toolkit) {
    return;
  }

  toolkit.setSuggestions([]);
}
